/**
 * Registry of all system feature modules available for plan configuration.
 * Each entry describes a module that a superadmin can include/exclude when
 * building subscription plans. Third-party integrations are configured
 * separately on the plan form (catalog tree + integrationIds), not as a
 * module tile here.
 *
 * Fields:
 *  id          - stable string key referenced in plan.modules[]
 *  label       - human-readable name displayed in plan builder UI
 *  icon        - Boxicons class name for visual identification
 *  defaultOn   - whether the module is enabled by default when added to a plan
 *  description - short description shown in the plan builder
 */

export const SYSTEM_MODULES = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: "bx bx-home-circle",
    defaultOn: true,
    description: "Overview analytics and KPIs",
  },
  {
    id: "locations",
    label: "Locations / Branches",
    icon: "bx bx-map",
    defaultOn: true,
    description: "Manage physical branches and delivery zones",
  },
  {
    id: "menus",
    label: "Menus",
    icon: "bx bx-food-menu",
    defaultOn: true,
    description: "Categories, items, modifiers, combos and item groups",
  },
  {
    id: "orders",
    label: "Orders",
    icon: "bx bx-cart",
    defaultOn: true,
    description: "F&B and B2C order management",
  },
  {
    id: "customers",
    label: "Customer Management",
    icon: "bx bx-user",
    defaultOn: true,
    description: "Customer profiles, loyalty points and activity",
  },
  {
    id: "roles",
    label: "Roles & Users",
    icon: "bx bx-briefcase",
    defaultOn: true,
    description: "Staff user accounts and role-based permissions",
  },
  {
    id: "user_activity",
    label: "User Activity",
    icon: "bx bx-list-check",
    defaultOn: false,
    description: "Audit trail of staff actions and login history",
  },
  {
    id: "promotions",
    label: "Promotions & Coupons",
    icon: "bx bx-purchase-tag",
    defaultOn: false,
    description: "Discount rules, coupon groups and redemption",
  },
  {
    id: "loyalty",
    label: "Loyalty Programme",
    icon: "bx bx-star",
    defaultOn: false,
    description: "Point earning rules and reward redemption",
  },
  {
    id: "gift_cards",
    label: "Gift Cards",
    icon: "bx bx-gift",
    defaultOn: false,
    description: "Digital gift card issuance and usage tracking",
  },
  {
    id: "bookings",
    label: "Bookings",
    icon: "bx bx-calendar",
    defaultOn: false,
    description: "Table and resource reservation management",
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: "bx bx-bell",
    defaultOn: false,
    description: "In-app and push notification campaigns",
  },
  {
    id: "campaign",
    label: "Marketing Campaigns",
    icon: "bx bx-bullhorn",
    defaultOn: false,
    description: "Targeted marketing campaigns and segmentation",
  },
  {
    id: "customer_support",
    label: "Customer Support",
    icon: "bx bx-support",
    defaultOn: false,
    description: "Issue tickets and support conversation tracking",
  },
  {
    id: "reports",
    label: "Reports & Analytics",
    icon: "bx bx-bar-chart-alt-2",
    defaultOn: false,
    description: "Sales, payment and business tax reports",
  },
  {
    id: "api_logs",
    label: "API Logs",
    icon: "bx bx-code-block",
    defaultOn: false,
    description: "Third-party integration request/response logs",
  },
  {
    id: "pos_settings",
    label: "POS Integration",
    icon: "bx bx-slider",
    defaultOn: false,
    description: "Point-of-sale sync and configuration",
  },
  {
    id: "banners",
    label: "Banners",
    icon: "bx bx-image",
    defaultOn: false,
    description: "Sliding banner and promotional image management",
  },
  {
    id: "offer_banners",
    label: "Offer Banners",
    icon: "bx bx-purchase-tag",
    defaultOn: false,
    description: "Offer and promotional banner management",
  },
  {
    id: "qr_codes",
    label: "QR Codes",
    icon: "bx bx-qr-scan",
    defaultOn: false,
    description: "Dynamic link and QR code generation",
  },
  {
    id: "dynamic_link",
    label: "Dynamic Links",
    icon: "bx bx-link",
    defaultOn: false,
    description: "Deep-link configuration for mobile apps",
  },
  {
    id: "vehicles",
    label: "Vehicles",
    icon: "bx bx-car",
    defaultOn: false,
    description: "Delivery vehicle and driver management",
  },
  {
    id: "brand_management",
    label: "Brand Management",
    icon: "bx bx-store",
    defaultOn: false,
    description: "Manage tenant brands, integrations and verification",
  },
  {
    id: "delivery_partners",
    label: "Delivery Partners",
    icon: "bx bx-cycling",
    defaultOn: false,
    description: "Delivery partner profiles, verification and settlements",
  },
  {
    id: "settings",
    label: "App Settings",
    icon: "bx bx-cog",
    defaultOn: true,
    description: "Branding, referral, allergens, social login and general settings",
  },
  {
    id: "billing",
    label: "Billing & Subscription",
    icon: "bx bx-credit-card",
    defaultOn: false,
    description: "Subscription overview, plan changes and invoices",
  },
]

export default SYSTEM_MODULES
